import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import {
  NotiflowWithSteps,
  NotiflowStepInput,
} from "@/types";
import { api, UnprocessableEntityError } from "@/utils/api";
import { camelize, underscore } from "@/utils/api-helpers";

export type UpdateNotiflowStepsInput = {
  steps: NotiflowStepInput[];
  /**
   * Content sha of the notiflow version that was loaded into the designer.
   * Sent to the API as `expected_sha` (via `underscore`) for optimistic
   * concurrency. When omitted, the save is forced (overwrites whatever is
   * currently on the server).
   */
  expectedSha?: string;
};

export const useUpdateNotiflowSteps = (
  projectId: string,
  notiflowId: string,
  options: Omit<
    UseMutationOptions<
      NotiflowWithSteps,
      UnprocessableEntityError,
      UpdateNotiflowStepsInput,
      unknown
    >,
    "mutationFn"
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<NotiflowWithSteps, UnprocessableEntityError, UpdateNotiflowStepsInput, unknown>({
    mutationFn: async (input: UpdateNotiflowStepsInput): Promise<NotiflowWithSteps> => {
      const response = await api.patch<NotiflowWithSteps>(`/projects/${projectId}/notiflows/${notiflowId}/steps`, underscore(input));
      return camelize(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "notiflows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "notiflows", notiflowId],
      });
    },
    ...options
  });
};
